import { BooleanOperator, ComparisonOperator, PolicyType, PolicyTypeExtended, PolicyTypeFilter, ServiceType } from '@common/enums';
import { PageState, PageStateFilter } from '@common/models';
import { UsersFilter } from '@common/services';
import { containsWrapper } from '@common/utils/functions/search';

export class FilterHelper {
  static applyProviderState(filters: string[], state: PageState): void {
    const { providerState, inBackup, totalSize } = state;

    switch (providerState) {
      case UsersFilter.All: {
        if (inBackup === false && totalSize) {
          filters.push(`TotalSize ${ComparisonOperator.Eq} 0`);
        }
        break;
      }
      case UsersFilter.NotInDomain: {
        filters.push(`UserProviderState ${ComparisonOperator.Eq} '${providerState}'`);

        if (totalSize) {
          filters.push(`TotalSize ${ComparisonOperator.Gt} 0`);
        }
        break;
      }
      case UsersFilter.InDomain:
      case UsersFilter.Suspended: {
        filters.push(`UserProviderState ${ComparisonOperator.Eq} '${providerState}'`);
        break;
      }
      default:
        console.error(`Caught error manually: Unknown type: "${providerState}"`);
    }
  }

  static applySearch(filters: string[], searchTerm?: string): void {
    if (!searchTerm) return;

    filters.push(`(${containsWrapper('Name', searchTerm)} ${BooleanOperator.Or} ${containsWrapper('Email', searchTerm)})`);
  }

  static applyServices(filters: string[], options: { serviceFilters: PageStateFilter; providerStateValue: string }): void {
    const { serviceFilters, providerStateValue } = options;
    const {
      [ServiceType.Calendar]: calendarFilter,
      [ServiceType.Contacts]: contactFilter,
      [ServiceType.Drive]: driveFilter,
      [ServiceType.Mail]: mailFilter
    } = serviceFilters;

    FilterHelper.addServiceFilter(filters, { filter: calendarFilter, field: 'CalendarEnable' });
    FilterHelper.addServiceFilter(filters, { filter: contactFilter, field: 'ContactsEnable' });
    FilterHelper.addServiceFilter(filters, { filter: driveFilter, field: 'DriveEnable' });
    FilterHelper.addServiceFilter(filters, { filter: mailFilter, field: 'MailEnable' });

    const anyServiceFilterApplied = mailFilter != null || driveFilter != null || contactFilter != null || calendarFilter != null;

    if (anyServiceFilterApplied) {
      filters.push(`UserProviderState ${ComparisonOperator.Ne} '${providerStateValue}'`);
    }
  }

  private static addServiceFilter(filters: string[], options: { filter: string | null; field: string }): void {
    const { filter, field } = options;

    if (filter == null) return;

    if (filter) {
      filters.push(`${field} ${ComparisonOperator.Eq} ${filter}`);
    } else {
      filters.push(
        `${field} ${ComparisonOperator.Eq} ${filter} ${BooleanOperator.Or} UserProviderState ${ComparisonOperator.Eq} '${UsersFilter.NotInDomain}' ${BooleanOperator.Or} InBackup ${ComparisonOperator.Eq} false`
      );
    }
  }

  static applyInBackup(filters: string[], state: PageState): void {
    const { inBackup, filter } = state;
    const {
      [ServiceType.Calendar]: calendarFilter,
      [ServiceType.Contacts]: contactFilter,
      [ServiceType.Drive]: driveFilter,
      [ServiceType.Mail]: mailFilter
    } = filter;

    const anyServiceFilterApplied = mailFilter != null || driveFilter != null || contactFilter != null || calendarFilter != null;

    if (anyServiceFilterApplied) {
      filters.push(`InBackup ${ComparisonOperator.Eq} ${inBackup === false ? 'false' : 'true'}`);
      return;
    }

    if (inBackup !== null) {
      filters.push(`InBackup ${ComparisonOperator.Eq} ${inBackup}`);
    }
  }

  static applyPolicyType(filters: string[], options: { policyType: PolicyTypeExtended; isOffice: boolean; isGoogle: boolean }): void {
    const { policyType, isOffice, isGoogle } = options;
    const fields = FilterHelper.getPolicyFilterFields({ isOffice, isGoogle });

    switch (policyType) {
      case PolicyTypeExtended.None:
        return void filters.push(FilterHelper.buildPolicyFilter(fields, PolicyType.None, ComparisonOperator.Eq, BooleanOperator.And));
      case PolicyTypeExtended.Custom:
        return void filters.push(FilterHelper.buildPolicyFilter(fields, PolicyType.Custom, ComparisonOperator.Eq, BooleanOperator.Or));
      case PolicyTypeExtended.Default: {
        const anyDefault = FilterHelper.buildPolicyFilter(fields, PolicyType.Default, ComparisonOperator.Eq, BooleanOperator.Or);
        const noCustom = FilterHelper.buildPolicyFilter(fields, PolicyType.Custom, ComparisonOperator.Ne, BooleanOperator.And);

        return void filters.push(`(${anyDefault}) ${BooleanOperator.And} (${noCustom})`);
      }
      case PolicyTypeExtended.All:
      default:
        return void 0;
    }
  }

  private static buildPolicyFilter(fields: string[], policy: PolicyType, operator: ComparisonOperator, separator: BooleanOperator): string {
    return fields.map((field) => `${field} ${operator} '${policy}'`).join(` ${separator} `);
  }

  private static getPolicyFilterFields(options: { isOffice: boolean; isGoogle: boolean }): string[] {
    const { isOffice, isGoogle } = options;
    const fields = [PolicyTypeFilter.Mail, PolicyTypeFilter.Drive, PolicyTypeFilter.Contact, PolicyTypeFilter.Calendar];

    if (isOffice) {
      fields.push(PolicyTypeFilter.SharePoint);
    }

    if (isGoogle) {
      fields.push(PolicyTypeFilter.TeamDrives);
    }

    return fields;
  }

  static applyIncludePolicyData(value: boolean): { key: string; value: boolean } {
    return { key: 'includePolicyData', value };
  }
}
